import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { ResetIntegrationService } from '../../../core/api-services/reset-integration.service';
import { ApiError } from '../../../abstract/api-error';
import { CustomClaimService } from '../../../core/services/custom-claim.service';
import { ResetHistoryItem } from '../../../core/response-models/reset-history-item.model';

/**
 * Reset History Component
 *
 * Displays a paginated table of past reset integration operations.
 * Only accessible to users with superAdmin claim.
 *
 * @see agent-os/specs/2026-01-25-reset-integration-data/spec.md
 */
@Component({
  selector: 'app-reset-history',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './reset-history.component.html',
  styleUrl: './reset-history.component.scss',
})
export class ResetHistoryComponent implements OnInit {
  historyItems: ResetHistoryItem[] = [];
  loading = true;
  errorMessage = '';

  // Pagination
  currentPage = 1;
  pageSize = 25;
  totalItems = 0;
  totalPages = 0;

  constructor(
    private router: Router,
    private resetService: ResetIntegrationService,
    private customClaimService: CustomClaimService
  ) {}

  ngOnInit(): void {
    // Verify super admin access
    if (!this.customClaimService.isSuperAdmin()) {
      this.router.navigate(['/dashboard']);
      return;
    }

    this.loadHistory();
  }

  /**
   * Load history with current pagination
   */
  async loadHistory(): Promise<void> {
    this.loading = true;
    this.errorMessage = '';

    try {
      const page = await this.resetService.getHistory(this.currentPage, this.pageSize);
      this.historyItems = page.items;
      this.totalItems = page.paging.totalItems;
      this.totalPages = page.paging.totalPages;
    } catch (error: unknown) {
      console.error('Failed to load history:', error);
      this.errorMessage = ApiError.fromHttpError(error).message || 'Failed to load reset history';
    } finally {
      this.loading = false;
    }
  }

  /**
   * Navigate to page
   */
  goToPage(page: number): void {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.currentPage = page;
      this.loadHistory();
    }
  }

  /**
   * Get pagination range for display
   */
  get paginationRange(): number[] {
    const range: number[] = [];
    const maxButtons = 5;
    let start = Math.max(1, this.currentPage - Math.floor(maxButtons / 2));
    let end = Math.min(this.totalPages, start + maxButtons - 1);

    if (end - start < maxButtons - 1) {
      start = Math.max(1, end - maxButtons + 1);
    }

    for (let i = start; i <= end; i++) {
      range.push(i);
    }

    return range;
  }

  /**
   * Format date for display
   */
  formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  /**
   * Get status badge class
   */
  getStatusClass(status: ResetHistoryItem['status']): string {
    switch (status) {
      case 'completed':
        return 'bg-success';
      case 'partial_failure':
        return 'bg-warning text-dark';
      case 'started':
        return 'bg-info';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Get status label
   */
  getStatusLabel(status: ResetHistoryItem['status']): string {
    switch (status) {
      case 'completed':
        return 'Completed';
      case 'partial_failure':
        return 'Partial Failure';
      case 'started':
        return 'In Progress';
      default:
        return status;
    }
  }

  /**
   * Format categories for display
   */
  formatCategories(categories: string[]): string {
    if (categories.length <= 3) {
      return categories.join(', ');
    }
    return `${categories.slice(0, 3).join(', ')} +${categories.length - 3} more`;
  }

  /**
   * Navigate back to reset integration page
   */
  goToResetIntegration(): void {
    this.router.navigate(['/system/reset-integration']);
  }

  /**
   * Navigate to dashboard
   */
  goToDashboard(): void {
    this.router.navigate(['/dashboard']);
  }

  readonly Math = Math;
}
